import { useRef, useState } from "react"

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from "@coval/ui"

import {
  CreateResourceButton,
  SaveResourceButton,
} from "@/components/ResourceActionButton"
import type { TagResource } from "@/types_api/backend/transport/list_organization_tags_response"

import { getDefaultTagColor } from "../constants/tag-colors"
import { MAX_TAG_LENGTH } from "../constants/tag-constraints"
import { TagChip } from "./TagChip"
import { TagColorSwatchPicker } from "./TagColorSwatchPicker"

interface TagCatalogFormProps {
  tag?: TagResource
  tags: TagResource[]
  isPending: boolean
  isCatalogReady: boolean
  catalogError: string | null
  onRetryCatalog: () => void
  error: string | null
  onClose: () => void
  onSave: (name: string, color: string | null) => void
}

export function TagCatalogForm({
  tag,
  tags,
  isPending,
  isCatalogReady,
  catalogError,
  onRetryCatalog,
  error,
  onClose,
  onSave,
}: TagCatalogFormProps) {
  const [name, setName] = useState(tag?.name ?? "")
  const [color, setColor] = useState(tag?.color ?? null)
  // Keep the edit preview stable without changing an untouched persisted color.
  const [defaultColor] = useState(() =>
    getDefaultTagColor(tag?.name ?? "new tag"),
  )
  const [attempted, setAttempted] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const normalizedName = name.trim().toLowerCase()
  const previewColor =
    color ??
    (tag ? defaultColor : getDefaultTagColor(normalizedName || "new tag"))
  const nameError = !normalizedName
    ? "Enter a tag name."
    : Array.from(name).length > MAX_TAG_LENGTH ||
        Array.from(normalizedName).length > MAX_TAG_LENGTH
      ? "Use 200 characters or fewer."
      : tags.some((item) => item.id !== tag?.id && item.name === normalizedName)
        ? "A tag with this name already exists."
        : null
  const unchanged = tag
    ? normalizedName === tag.name && color === (tag.color ?? null)
    : false
  const revealValidation = () => {
    setAttempted(true)
    inputRef.current?.focus()
  }
  const submit = () => {
    if (isPending || unchanged || !isCatalogReady) {
      return
    }
    if (nameError) {
      revealValidation()
      return
    }
    onSave(normalizedName, color)
  }
  const Action = tag ? SaveResourceButton : CreateResourceButton

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <DialogContent size="md" data-testid="tag-catalog-form">
        <DialogHeader>
          <DialogTitle>{tag ? "Edit Tag" : "Create Tag"}</DialogTitle>
          <DialogDescription>
            {tag
              ? "Changes apply everywhere this tag is used in the workspace."
              : "Use the same tag across runs, agents, test sets, personas, metrics, and run templates."}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <div className="space-y-5 px-6 py-4">
            <div className="flex items-start gap-3">
              <FormField
                label="Name"
                className="min-w-0 flex-1"
                error={attempted ? nameError : null}
              >
                {(ids) => (
                  <Input
                    {...ids}
                    ref={inputRef}
                    value={name}
                    disabled={isPending}
                    placeholder="e.g. needs-review"
                    onChange={(event) => setName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault()
                        submit()
                      }
                    }}
                    data-testid="tag-name-input"
                  />
                )}
              </FormField>
              <FormField label="Color" className="w-20 shrink-0">
                <TagColorSwatchPicker
                  tag={normalizedName || "new tag"}
                  color={previewColor}
                  onColorChange={setColor}
                  disabled={isPending}
                  variant="field"
                />
              </FormField>
            </div>
            <Card className="space-y-3 p-4" aria-label="Tag preview">
              <p className="text-xs font-medium uppercase tracking-wide text-coval-text-muted">
                Preview
              </p>
              <TagChip
                tag={normalizedName || "your-tag"}
                color={previewColor}
              />
            </Card>
            {catalogError && (
              <Alert variant="danger">
                <AlertDescription>
                  {catalogError}{" "}
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={isPending}
                    onClick={onRetryCatalog}
                  >
                    Retry catalog
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            {error && (
              <Alert variant="danger">
                <AlertDescription>
                  {error} You can retry saving.
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              disabled={isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Action
              type="submit"
              resourceName="tag"
              loading={isPending}
              disabled={unchanged || !isCatalogReady}
              validationIssues={[
                nameError,
                !isCatalogReady ? catalogError : null,
              ]}
              onValidationAttempt={revealValidation}
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
